/** Pull a plain `{ header, rows }` table out of the hast (HTML AST) node that
 *  react-markdown hands the `table` component override in `MarkdownView.tsx`.
 *  Pure: no React, no DOM, no I/O — so the export writers can be unit tested
 *  without rendering anything.
 *
 *  Reading the hast rather than the rendered DOM is deliberate (see
 *  `docs/superpowers/specs/2026-09-22-chat-table-exports-design.md`, Decision 3):
 *  the node is already in hand at the single override that covers all three
 *  chat-ui mount points. */

// The cell shape is the export layer's contract, so it is owned there and
// imported here (components depend on lib, never the reverse). The export
// writers take `{ header: TableCell[]; rows: TableCell[][] }` and so need no
// import from this component directory.
import type { TableCell } from "@/lib/export/coerce";

/** Minimal HAST node shape we touch. Mirrors the local interface in
 *  `MarkdownView.tsx:97-103` — that one is not exported, and this module must
 *  not import from a React component (it would drag MUI/KaTeX into a pure
 *  test). Exported here so the component can adopt it and drop its copy. */
export interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: { className?: unknown; href?: unknown };
  children?: HastNode[];
}

export interface ExtractedTable {
  header: TableCell[];
  rows: TableCell[][];
}

const CELL_TAGS = new Set(["th", "td"]);

/** Every text node in the subtree, in document order.
 *
 *  A cell's text is NOT `children[0].value`. While a turn is streaming,
 *  `rehypeWordFade` (`MarkdownView.tsx:144-162`) rewrites each word into its own
 *  `<span class="md-word">`, and `isFadeExcluded` (`:113`) exempts only
 *  code/pre/katex — table cells are wrapped. Inline markdown (`<strong>`,
 *  `<code>`, `<a>`) nests text arbitrarily deep too. So: recurse. */
function textOf(node: HastNode): string {
  if (node.type === "text") return node.value ?? "";
  let out = "";
  for (const child of node.children ?? []) out += textOf(child);
  return out;
}

/** The `href` of the first `<a>` in the subtree, in document order.
 *
 *  The URL survives nowhere else: remark resolved `[label](url)` into an
 *  element before this tree existed, so a text-only read of the cell loses it
 *  and the spec's "URL attached as a real hyperlink" quietly never happens.
 *
 *  First, not all: a spreadsheet cell holds one hyperlink, and the first is the
 *  one whose label leads the cell's text. */
function firstHref(node: HastNode): string | undefined {
  if (node.type === "element" && node.tagName === "a") {
    const href = node.properties?.href;
    if (typeof href === "string" && href !== "") return href;
  }
  for (const child of node.children ?? []) {
    const href = firstHref(child);
    if (href !== undefined) return href;
  }
  return undefined;
}

/** One cell: its text with whitespace collapsed the way a browser renders it,
 *  so the exported value matches what the user saw (the fade spans re-emit
 *  inter-word spaces as their own text nodes, and remark leaves newlines in the
 *  tree), plus the link the text alone cannot carry. */
function readCell(node: HastNode): TableCell {
  const cell: TableCell = { text: textOf(node).replace(/\s+/g, " ").trim() };
  const href = firstHref(node);
  if (href !== undefined) cell.href = href;
  return cell;
}

/** Cells of one `<tr>`, without descending into a cell's own content. */
function collectCells(node: HastNode, out: TableCell[]): void {
  for (const child of node.children ?? []) {
    if (child.type !== "element") continue;
    if (CELL_TAGS.has(child.tagName ?? "")) out.push(readCell(child));
    else collectCells(child, out);
  }
}

/** Every `<tr>` in the table, split by whether it sits under a `<thead>`.
 *  Walking generically (rather than assuming thead/tbody are direct children)
 *  keeps this working for hand-written HTML tables as well as GFM output. */
function collectRows(
  node: HastNode,
  inHead: boolean,
  head: TableCell[][],
  body: TableCell[][],
): void {
  for (const child of node.children ?? []) {
    if (child.type !== "element") continue;
    if (child.tagName === "tr") {
      const cells: TableCell[] = [];
      collectCells(child, cells);
      (inHead ? head : body).push(cells);
    } else {
      collectRows(child, inHead || child.tagName === "thead", head, body);
    }
  }
}

/**
 * Extract a table from a `<table>` hast node, or `null` when it holds no cells.
 *
 * With no `<thead>`, the first row becomes the header — the way a spreadsheet
 * import reads a header-less sheet. Leaving the header empty would instead put
 * a blank row at A1, which breaks Excel's "Format as Table" and pivots.
 *
 * Rows are padded to a rectangle. A short row gains empty cells; an over-long
 * row widens the whole table rather than being cut, because an export must not
 * silently drop data.
 */
export function extractTable(node: HastNode): ExtractedTable | null {
  const head: TableCell[][] = [];
  const body: TableCell[][] = [];
  collectRows(node, false, head, body);

  // Extra `<thead>` rows (never emitted by GFM, possible in raw HTML) are kept
  // as data rather than discarded.
  const header = head.length > 0 ? head[0] : (body.shift() ?? []);
  const rows = [...head.slice(1), ...body];

  const width = rows.reduce((w, row) => Math.max(w, row.length), header.length);
  if (width === 0) return null;

  const pad = (row: TableCell[]) =>
    row.length === width
      ? row
      : [
          ...row,
          ...Array.from({ length: width - row.length }, () => ({ text: "" })),
        ];

  return { header: pad(header), rows: rows.map(pad) };
}
